import produce from 'immer';
import { useCallback } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useDispatch, useSelector } from 'react-redux';

import { type DropdownMenuOption } from '@air/primitive-dropdown-menu';
import { useAirModal } from '@air/provider-modal';
import { resetSelectedItemsAction, selectedItemsCountSelector } from '@air/redux-selected-items';

import { type GetSelectionMenuOptionsType } from '~/components/Gallery/types';
import { useAddAssetsToBoardsModal } from '~/components/Modals/AddAssetsToBoardsModal/useAddAssetsToBoardsModal';
import { useAddToCanvasModal } from '~/components/Modals/AddToCanvasModal/useAddToCanvasModal';
import { useDuplicateBoard } from '~/components/Modals/DuplicateBoard/hooks/useDuplicateBoard';
import { useMoveItemsModal } from '~/components/Modals/MoveItemsModal/useMoveItemsModal';
import { useRemoveAssetsFromBoardModal } from '~/components/Modals/RemoveClipsFromBoardModal/hooks/useRemoveAssetsFromBoardModal';
import { PrivateContextSourcesAddClipModal } from '~/components/v2/components/context-sources/modals/PrivateContextSourcesAddClipModal';
import { useCanEditContextSources } from '~/components/v2/hooks/useCanEditContextSources';
import { useWorkspaceRightPanelStore } from '~/components/v2/store/features/workspace/useWorkspaceRightPanelStore';
import { useIsSearchActive } from '~/hooks/search/useIsSearchActive';
import { useAddToCollectionOption } from '~/hooks/useAddToCollectionOption';
import { useAssetPermissionsCache } from '~/hooks/useAssetPermissionsCache';
import { useBoardPermissionsCache } from '~/hooks/useBoardPermissionsCache';
import { useClipPermissionsCache } from '~/hooks/useClipPermissionsCache';
import { useGetPrivateCustomFieldsMenuOptions } from '~/hooks/useGetCustomFieldsMenuOptions';
import { useHandleDeletePrivateSelection } from '~/hooks/useHandleDeletePrivateSelection';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { CentralizedBoardSelectors, centralizedBoardTitleSelector } from '~/store/centralizedBoard/selectors';
import { isFlattenedViewSelector } from '~/store/router/selectors';
import {
  hasOnlyBoardsSelectedSelector,
  hasOnlyClipsSelectedSelector,
  hasSelectionAcrossMultipleViewsSelector,
  selectedBoardIdsForCollectionSelector,
  selectedBoardsSelector,
  selectedClipsAssetIdsSelector,
  selectedClipsIdsInfoSelector,
  selectedClipsSelector,
  selectedPrivateBoardsSelector,
} from '~/store/selectedItems/selectors';
import { useGetBoardContainerPermissions } from '~/swr-hooks/boards/useGetBoardContainerPermissions';
import { getDropdownMenuOption } from '~/utils/menuOptions/getDropdownMenuOption';
import {
  canAddAssetToBoard,
  canChangeAssetCustomFields,
  canDeleteAsset,
  canMoveAsset,
} from '~/utils/permissions/assetPermissions';
import {
  canChangeBoardCustomFields,
  canDeleteBoard,
  canDuplicateBoard,
  canRemoveAssetsFromBoard,
} from '~/utils/permissions/boardPermissions';
import { useAirStore } from '~/utils/ReduxUtils';

export const usePrivateSelectionMenuOptions = () => {
  const { isSearchActive } = useIsSearchActive();

  const { showRemoveAssetsFromBoardModal } = useRemoveAssetsFromBoardModal();
  const { showAddAssetsToBoardsModal } = useAddAssetsToBoardsModal();
  const { showDuplicateBoardsModal } = useDuplicateBoard();
  const { showMoveItemsModal } = useMoveItemsModal();

  // Submenu options
  const { getCustomFieldsMenuOptions } = useGetPrivateCustomFieldsMenuOptions();
  const getAddToCollectionOption = useAddToCollectionOption();
  const { showAddToCanvasModal } = useAddToCanvasModal();
  const [showAddContextSourceClipModal] = useAirModal(PrivateContextSourcesAddClipModal);
  const { canEditContextSources } = useCanEditContextSources();

  const store = useAirStore();
  const dispatch = useDispatch();
  const { getClipPermissions } = useClipPermissionsCache();
  const { getBoardPermissions } = useBoardPermissionsCache();
  const { getAssetPermissions } = useAssetPermissionsCache();
  const { getBoardContainerPermissions } = useGetBoardContainerPermissions();
  const { data: workspacePermissions } = useCurrentWorkspacePermissionsContext();
  const handleDelete = useHandleDeletePrivateSelection();
  const canvasOpen = useWorkspaceRightPanelStore((state) => !!state.selectedCanvasId);
  const ignoreWhenCanvasOpen = useCallback(() => canvasOpen, [canvasOpen]);

  // Use selectors for hotkey enable check
  const selectedCount = useSelector(selectedItemsCountSelector);
  const selectedBoards = useSelector(selectedBoardsSelector);
  const selectedClipsInfo = useSelector(selectedClipsIdsInfoSelector);
  const currentBoardId = useSelector(CentralizedBoardSelectors.boardId);

  const getSelectionMenuOptions: GetSelectionMenuOptionsType = useCallback(
    (trackLocation) => {
      const currentBoardId = CentralizedBoardSelectors.boardId(store.getState());
      const currentBoardPermissions = currentBoardId ? getBoardPermissions(currentBoardId) : undefined;
      const boardTitle = centralizedBoardTitleSelector(store.getState());
      const isFlattenedView = isFlattenedViewSelector(store.getState()) === 'true';

      const hasOnlyClipsSelected = hasOnlyClipsSelectedSelector(store.getState());
      const hasOnlyBoardsSelected = hasOnlyBoardsSelectedSelector(store.getState());
      const selectedCount = selectedItemsCountSelector(store.getState());
      const selectedClipsInfo = selectedClipsIdsInfoSelector(store.getState());
      const selectedClipIds = selectedClipsInfo.map(({ id }) => id);
      const selectedBoards = selectedBoardsSelector(store.getState());
      const selectedPrivateBoards = selectedPrivateBoardsSelector(store.getState());
      const hasSelectionAcrossMultipleViews = hasSelectionAcrossMultipleViewsSelector(store.getState());

      const selectedClips = selectedClipsSelector(store.getState());

      const canMoveAllBoards = selectedBoards.every((board) => canDeleteBoard(getBoardContainerPermissions({ board })));

      const canMoveAllAssets = selectedClips.every(({ assetId }) => canMoveAsset(getAssetPermissions(assetId)));
      const canEditAllClipCustomFields = selectedClips.every(({ id }) =>
        canChangeAssetCustomFields(getClipPermissions(id)),
      );
      const canEditAllBoardCustomFields = selectedBoards.every(({ id }) =>
        canChangeBoardCustomFields(getBoardPermissions(id)),
      );

      const canEditCustomFields = canEditAllClipCustomFields && canEditAllBoardCustomFields;
      const canRemoveAssets = currentBoardId && canRemoveAssetsFromBoard(currentBoardPermissions);

      if (selectedCount < 2) {
        return [];
      }

      const options = produce<DropdownMenuOption[]>([], (draft) => {
        const addToSubItems: DropdownMenuOption[] = [];

        if (hasOnlyClipsSelected) {
          const canAddAllAssetsToABoard = selectedClips.every(({ assetId }) =>
            canAddAssetToBoard(getAssetPermissions(assetId)),
          );

          if (canAddAllAssetsToABoard) {
            addToSubItems.push({
              ...getDropdownMenuOption('add-to-board'),
              onSelect: () =>
                showAddAssetsToBoardsModal({
                  clipIds: selectedClipIds,
                  trackLocation,
                  parentBoardId: currentBoardId,
                }),
              label: `Add ${selectedCount} to board`,
              type: 'item',
            });
          }

          addToSubItems.push({
            ...getDropdownMenuOption('add-to-canvas'),
            onSelect: () => showAddToCanvasModal({ clips: selectedClips }),
            type: 'item',
          });

          if (canEditContextSources && selectedClips.length === 1) {
            addToSubItems.push({
              ...getDropdownMenuOption('add-to-context'),
              onSelect: () => showAddContextSourceClipModal({ clip: selectedClips[0] }),
              type: 'item',
            });
          }
        }

        if (getAddToCollectionOption) {
          const addToCollectionOption = getAddToCollectionOption({
            assetIds: selectedClipsAssetIdsSelector(store.getState()),
            boardIds: selectedBoardIdsForCollectionSelector(store.getState()),
          });

          if (addToCollectionOption) {
            addToSubItems.push(addToCollectionOption);
          }
        }

        if (addToSubItems.length > 0) {
          if (draft.length > 0) {
            draft.push({ id: 'add-to-separator', type: 'separator' });
          }
          draft.push({ ...getDropdownMenuOption('add-to'), options: addToSubItems, type: 'sub' });
        }

        const canMove = !!currentBoardId && !isSearchActive;

        /**
         * 'Move' option should show if user is in search and boards and assets are selected,
         * or if anything is selected while selecting in a board
         */
        const shouldShowMoveOption = (canMoveAllBoards && !hasOnlyClipsSelected && !canMove) || canMove;

        if (
          !isFlattenedView &&
          shouldShowMoveOption &&
          canMoveAllAssets &&
          canMoveAllBoards &&
          currentBoardId &&
          !hasSelectionAcrossMultipleViews
        ) {
          draft.push({
            ...getDropdownMenuOption('move'),
            onSelect: () =>
              showMoveItemsModal({
                boards: selectedPrivateBoards,
                clips: selectedClipsInfo,
                onMove: () => dispatch(resetSelectedItemsAction()),
                trackLocation,
              }),
            label: `Move ${selectedCount} to board`,
            type: 'item',
          });
        }

        if (canEditCustomFields && getCustomFieldsMenuOptions) {
          const customFieldsMenuOptions = getCustomFieldsMenuOptions({
            boards: selectedBoards,
            assets: selectedClips,
          });

          if (customFieldsMenuOptions.length) {
            if (draft.length > 0) {
              draft.push({ id: 'edit-separator', type: 'separator' });
            }
            draft.push({
              ...getDropdownMenuOption('bulk-edit-custom-fields'),
              options: customFieldsMenuOptions,
              type: 'sub',
            });
          }
        }

        const canDuplicateAllBoards = selectedBoards.every((board) =>
          canDuplicateBoard({
            boardContext: getBoardPermissions(board.id),
            targetContext: getBoardContainerPermissions({ board }),
          }),
        );

        if (hasOnlyBoardsSelected && canDuplicateAllBoards) {
          draft.push({
            ...getDropdownMenuOption('duplicate'),
            onSelect: () => showDuplicateBoardsModal({ boards: selectedPrivateBoards, trackLocation }),
            label: `Duplicate ${selectedCount}`,
            type: 'item',
          });
        }

        const isRemoveFromBoardVisible =
          hasOnlyClipsSelected &&
          !isFlattenedView &&
          currentBoardId &&
          boardTitle &&
          canRemoveAssets &&
          !hasSelectionAcrossMultipleViews;

        if (isRemoveFromBoardVisible) {
          draft.push({
            id: 'delete-separator',
            type: 'separator',
          });
          draft.push({
            ...getDropdownMenuOption('remove-from-board'),
            label: `Remove ${selectedCount} from board`,
            onSelect: () =>
              showRemoveAssetsFromBoardModal({
                boardId: currentBoardId,
                boardTitle,
                clipIds: selectedClipIds,
                trackLocation,
              }),
            type: 'item',
          });
        }

        const canDeleteAllAssets =
          selectedClips.length > 0
            ? canDeleteAsset(currentBoardId ? currentBoardPermissions : workspacePermissions)
            : true;
        const canDeleteAllBoards =
          selectedBoards.length > 0
            ? selectedBoards.every((board) => canDeleteBoard(getBoardContainerPermissions({ board })))
            : true;

        if (canDeleteAllAssets && canDeleteAllBoards) {
          if (!isRemoveFromBoardVisible) {
            draft.push({
              id: 'delete-separator',
              type: 'separator',
            });
          }

          draft.push({
            ...getDropdownMenuOption('delete'),
            label: 'Delete all from workspace',
            onSelect: () => handleDelete(trackLocation),
            type: 'item',
          });
        }

        return draft;
      });

      return options;
    },
    [
      store,
      getBoardPermissions,
      getBoardContainerPermissions,
      getClipPermissions,
      getAssetPermissions,
      isSearchActive,
      getCustomFieldsMenuOptions,
      getAddToCollectionOption,
      workspacePermissions,
      showAddAssetsToBoardsModal,
      dispatch,
      showMoveItemsModal,
      showDuplicateBoardsModal,
      showRemoveAssetsFromBoardModal,
      showAddToCanvasModal,
      showAddContextSourceClipModal,
      canEditContextSources,
      handleDelete,
    ],
  );

  // Memoized enabled check for hotkey
  const isHotkeyEnabled = useCallback(() => {
    // Only enable if items are selected
    if (selectedCount === 0) {
      return false;
    }

    // Check permissions
    const currentBoardPermissions = currentBoardId ? getBoardPermissions(currentBoardId) : undefined;

    const canDeleteAllAssets =
      selectedClipsInfo.length > 0
        ? canDeleteAsset(currentBoardId ? currentBoardPermissions : workspacePermissions)
        : true;
    const canDeleteAllBoards =
      selectedBoards.length > 0
        ? selectedBoards.every((board) => canDeleteBoard(getBoardContainerPermissions({ board })))
        : true;

    return canDeleteAllAssets && canDeleteAllBoards;
  }, [
    selectedCount,
    selectedClipsInfo.length,
    selectedBoards,
    currentBoardId,
    getBoardPermissions,
    getBoardContainerPermissions,
    workspacePermissions,
  ]);

  useHotkeys(
    'Backspace, delete, del',
    () => {
      handleDelete('keyboard');
    },
    {
      enabled: isHotkeyEnabled,
      ignoreEventWhen: ignoreWhenCanvasOpen,
      scopes: ['gallery'],
      enableOnFormTags: false,
    },
    [handleDelete, isHotkeyEnabled, ignoreWhenCanvasOpen],
  );

  return {
    getSelectionMenuOptions,
  };
};
